import { useState, useEffect } from "react";
import TextInput from "../components/ui/Input/TextInput";
import useFetch from "../hooks/useFetch";
import styles from "../components/EntityAddUpdateForm/EntityAddUpdateForm.module.css";

const EditProfile: React.FC = () => {
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true); // Loading state
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    data: userData,
    errorContent,
    fetchData: fetchUserData,
  } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/users/0`,
    method: "GET",
  });

  // Fetch user data when component mounts
  useEffect(() => {
    const fetch = async () => {
      setLoading(true); // Set loading to true before fetching data
      await fetchUserData();
      setLoading(false); // Set loading to false after data is fetched
    };
    fetch();
  }, []);

  // Populate state when data is fetched
  useEffect(() => {
    if (userData) {
      setFirstName(userData.first_name || "");
      setLastName(userData.last_name || "");
    }
  }, [userData]);

  if (errorContent) return <p className="error-message">{errorContent}</p>;

  const handleEditProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!firstName || !lastName) {
      setErrorMessage("Firstname and Latname is mandatory.");
      setSuccessMessage(null);
      return;
    }

      // Prepare the update data object
      const updateData: any = {
        first_name: firstName,
        last_name: lastName,
      };

      // Only add password fields if they are not empty
      if (currentPassword && newPassword) {
        updateData.current_password = currentPassword;
        updateData.new_password = newPassword;
      }

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await fetch(
        //`${import.meta.env.VITE_API_URL}/users/edit-profile`,
        `${import.meta.env.VITE_API_URL}/users/change-password`,        
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSuccessMessage("Details updated successfully!");
        setErrorMessage(null);
      } else {
        const errorMessages = result.errors || [];
        setErrorMessage(
          errorMessages[0]?.error || result.message || "Failed to update details."
        );
        setSuccessMessage(null);
      }
    } catch (err) {
      setErrorMessage("An error occurred. Please try again.");
      setSuccessMessage(null);
    }
  };

  const handleCancel = () => {
    // Reset fields and clear messages
    setFirstName(userData?.first_name || "");
    setLastName(userData?.last_name || "");
    setCurrentPassword("");
    setNewPassword("");
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  if (loading) {
    return <p>loading...</p>;
  }

  return (
    <div className="center-container">
      <form
        onSubmit={handleEditProfile}
        className={styles.entityChangePasswordForm}
      >
        <h2 className="form-title">Update User Details</h2>
        <h5>
          {userData?.roles?.[0]?.role_name}: {userData?.email}
        </h5>
        <TextInput
          placeholder="First Name"
          value={firstName}
          onChange={(e) => {
            setErrorMessage(null);
            setSuccessMessage(null);
            setFirstName(e.target.value);
          }}
        />
        <TextInput
          placeholder="Last Name"
          value={lastName}
          onChange={(e) => {
            setErrorMessage(null);
            setSuccessMessage(null);
            setLastName(e.target.value);
          }}
        />
        <TextInput
          type="password"
          placeholder="Current Password"
          value={currentPassword}
          onChange={(e) => {
            setErrorMessage(null);
            setSuccessMessage(null);
            setCurrentPassword(e.target.value);
          }}
        />
        <TextInput
          type="password"
          placeholder="New Password"
          value={newPassword}
          onChange={(e) => {
            setErrorMessage(null);
            setSuccessMessage(null);
            setNewPassword(e.target.value);
          }}
        />

        {successMessage && <p className="success-message">{successMessage}</p>}
        {errorMessage && <p className="error-message">{errorMessage}</p>}

        {/* Buttons */}
        <div className="button-container">
          <button
            type="submit"
            className={styles.entityAddUpdateForm__submitbtn}
          >
            Update Details
          </button>
          &nbsp;&nbsp;&nbsp;
          <button
            type="button"
            className={styles.entityAddUpdateForm__submitbtn}
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProfile;
